<template>
    <form class="edit-password" @submit.prevent="set_password()">
        <label :for="password_id">New password</label>
        <div class="password-row">
            <input :id="password_id" :type="password_shown ? 'text' : 'password'" v-model="password"
                   autocomplete="new-password" placeholder="Type a new password"
                   :disabled="password_status=='saving'" @input="password_status=''">
            <button type="button" :title="password_shown ? 'Hide' : 'Show'"
                    :aria-label="password_shown ? 'Hide the password' : 'Show the password'"
                    @click.stop="password_shown=!password_shown">
                <span class="material-symbols-outlined">{{ password_shown ? 'visibility_off' : 'visibility' }}</span>
            </button>
            <button type="submit" :disabled="!password || password_status=='saving'">
                <span class="material-symbols-outlined">key</span>
                {{ password_status=='saving' ? 'Saving…' : 'Set password' }}
            </button>
        </div>
        <p v-if="password_status=='saved'" class="password-note ok" role="status">Password changed. Their old one no longer works.</p>
        <p v-else-if="password_status=='failed'" class="password-note error" role="alert">The password was not changed. Try again.</p>
        <p v-else class="password-note">Replaces their current password. The old one cannot be shown - only set a new one.</p>
    </form>
</template>


<script setup>

    import {ref} from 'vue'
    import axios from 'axios'

    const props=defineProps({
        user:{type:Object,required:true},      // needs only its id
    })

    let password=ref('')
    let password_shown=ref(false)
    let password_status=ref('')        // '' | saving | saved | failed
    const password_id=`password-${Math.random().toString(36).slice(2)}`


    // Admin.patch routes `password` to set_password(), so it is stored hashed and never sent back.
    async function set_password(){
        if(!password.value){
            return
        }
        password_status.value='saving'
        try{
            await axios.patch('/admin/patch/',{
                object:{endpoint:'User',id:props.user.id},
                key:'password',
                value:password.value,
            })
            password.value=''
            password_shown.value=false
            password_status.value='saved'
        }catch(e){
            password_status.value='failed'
        }
    }

</script>


<style scoped>

    .edit-password{
        grid-column:1 / -1;
        display:flex;
        flex-direction:column;
        gap:6px;
        margin:0;
    }

    .edit-password label{
        font-size:12.5px;
        font-weight:600;
        color:var(--ew-muted);
    }

    .password-row{
        display:flex;
        flex-wrap:wrap;
        gap:8px;
    }

    .password-row input{
        flex:1 1 180px;
        min-width:0;
        min-height:38px;
        padding:7px 10px;
        font-size:16px;     /* 16px stops iOS zooming in on focus */
        color:var(--ew-text);
        background:#ffffff;
        border:1px solid var(--ew-field-line);
        border-radius:6px;
    }

    .password-row input:focus{
        outline:2px solid var(--ew-focus);
        outline-offset:-1px;
    }

    button{
        display:inline-flex;
        align-items:center;
        gap:6px;
        min-height:38px;
        padding:7px 12px;
        font-size:13px;
        word-break:normal;
        color:var(--ew-text);
        background:#ffffff;
        border:1px solid var(--ew-field-line);
        border-radius:6px;
        box-shadow:none;
    }

    button:hover{
        color:var(--ew-text);
        background:var(--ew-bg);
        box-shadow:none;
    }

    button .material-symbols-outlined{
        font-size:18px;
    }

    button:disabled{
        opacity:.55;
        cursor:default;
    }

    .password-note{
        margin:0;
        font-size:12.5px;
        color:var(--ew-muted);
    }

    .password-note.ok{
        color:var(--ew-ok);
    }

    .password-note.error{
        color:var(--ew-error);
    }

</style>
